import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../Components/Navbar/AdminNavbar/AdminNavbar";
import { auth, db, storage as fbStorage } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  addDoc,
  getDocs,
  setDoc,
  query,
  updateDoc,
  where,
  serverTimestamp,
  GeoPoint,
  increment,
} from "firebase/firestore";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import "./BusinessPage.css";

console.log("BUSINESS PAGE RENDERED");

/* ---------------- Google Maps loader (JS + Places) ---------------- */
function loadGoogleMaps() {
  if (window.google?.maps) return Promise.resolve();
  if (window.__gmapsLoading) return window.__gmapsLoading;

  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY; // CRA env
  if (!key) {
    const msg = "Missing REACT_APP_GOOGLE_MAPS_API_KEY";
    console.error(msg);
    return Promise.reject(new Error(msg));
  }

  window.__gmapsLoading = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.onerror = () => reject(new Error("Failed to load Google Maps JS"));
    script.onload = () => resolve();
    document.head.appendChild(script);
  });

  return window.__gmapsLoading;
}

function AddressAutocomplete({ value, onChange, onPick }) {
  const inputRef = useRef(null);
  const acRef = useRef(null);

  useEffect(() => {
    let listener;
    let cancelled = false;

    loadGoogleMaps().then(() => {
      if (cancelled || !inputRef.current) return;

      acRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        fields: ["geometry", "formatted_address"],
        types: ["geocode"],
      });

      listener = acRef.current.addListener("place_changed", () => {
        const place = acRef.current.getPlace();
        if (!place) return;
        if (!place.geometry || !place.geometry.location) {
          console.warn("Place has no geometry", place);
          return;
        }
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        onChange?.(place.formatted_address || "");
        onPick?.(lat, lng, place.formatted_address || "");
      });
    }).catch((err) => console.error(err));

    return () => {
      cancelled = true;
      if (listener) {
        window.google?.maps?.event?.removeListener(listener);
      }
    };
  }, [onChange, onPick]);

  return (
    <input
      ref={inputRef}
      type="text"
      className="input"
      placeholder="Search for an address"
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
    />
  );
}

/* ---------------- MapPicker (embedded map + click-to-pick) ---------------- */
function MapPicker({ lat, lng, onPick }) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    loadGoogleMaps().then(() => {
      if (!mounted || !mapDivRef.current) return;

      const hasPoint = Number.isFinite(lat) && Number.isFinite(lng);
      const start = hasPoint ? { lat, lng } : { lat: 17.1274, lng: -61.8468 }; // Antigua-ish

      mapRef.current = new window.google.maps.Map(mapDivRef.current, {
        center: start,
        zoom: hasPoint ? 15 : 10,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      if (hasPoint) {
        markerRef.current = new window.google.maps.Marker({
          position: start,
          map: mapRef.current,
          draggable: true,
        });
        markerRef.current.addListener("dragend", (e) => {
          onPick?.(e.latLng.lat(), e.latLng.lng());
        });
      }

      mapRef.current.addListener("click", (e) => {
        const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        if (!markerRef.current) {
          markerRef.current = new window.google.maps.Marker({
            position: pos,
            map: mapRef.current,
            draggable: true,
          });
          markerRef.current.addListener("dragend", (ev) => {
            onPick?.(ev.latLng.lat(), ev.latLng.lng());
          });
        } else {
          markerRef.current.setPosition(pos);
        }
        onPick?.(pos.lat, pos.lng);
      });
    }).catch((err) => console.error(err));

    return () => {
      mounted = false;
    };
  }, [lat, lng, onPick]);

  return (
    <div className="gmapsRoot">
      <div ref={mapDivRef} className="gmapsCanvas" />
    </div>
  );
}

function createEmptyLocation() {
  return {
    country: "",
    island: "",
    region: "",
    address: "",
    telephone: "",
    email: "",
    telephones: [""],
    emails: [""],
    lat: "",
    lng: "",
  };
}

function normalizeContactList(values, legacyValue) {
  const list = Array.isArray(values) ? values : [legacyValue];
  const normalized = list.map((value) => String(value || "").trim()).filter(Boolean);
  return normalized.length ? normalized : [];
}

function readLat(location) {
  return location?.lat ?? location?.latitude ?? location?.location?.latitude ?? "";
}

function readLng(location) {
  return location?.lng ?? location?.longitude ?? location?.location?.longitude ?? "";
}

function normalizeLocation(location = {}) {
  const telephones = normalizeContactList(location.telephones, location.telephone);
  const emails = normalizeContactList(location.emails, location.email);
  return {
    country: (location.country || "").trim(),
    island: (location.island || "").trim(),
    region: (location.region || "").trim(),
    address: (location.address || location.locationLabel || "").trim(),
    telephone: telephones[0] || "",
    email: emails[0] || "",
    telephones,
    emails,
    lat: readLat(location),
    lng: readLng(location),
  };
}

function locationHasDetails(location) {
  return Boolean(
    location.country ||
      location.island ||
      location.region ||
      location.address ||
      location.telephone ||
      location.email ||
      location.lat ||
      location.lng
  );
}

function getBusinessLocations(business) {
  if (Array.isArray(business.locations) && business.locations.length > 0) {
    const normalized = business.locations.map(normalizeLocation).filter(locationHasDetails);
    if (normalized.length > 0) return normalized;
  }

  const fallback = normalizeLocation({
    country: business.country,
    island: business.island,
    address: business.locationLabel || business.location?.address,
    telephone: business.telephone,
    email: business.email,
    lat: business.location?.latitude,
    lng: business.location?.longitude,
  });

  return locationHasDetails(fallback) ? [fallback] : [];
}

function locationMapEmbed(location) {
  const latNum = parseFloat(readLat(location));
  const lngNum = parseFloat(readLng(location));

  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null;

  return `https://www.google.com/maps?q=${latNum},${lngNum}&hl=en&z=16&output=embed`;
}

/* ---------------- Category chips + selector ---------------- */
function CategoryPill({ allCats, selected, onChange }) {
  const selectedCats = useMemo(() => {
    const byId = new Map();
    allCats.forEach((c) => byId.set(c.id, c));
    return selected
      .map((id) => byId.get(id))
      .filter(Boolean)
      .map((c) => ({ id: c.id, label: c.name || "Unknown" }));
  }, [allCats, selected]);

  return (
    <div className="catPills">
      {selectedCats.length === 0 ? (
        <span className="chip chipEmpty">No categories selected</span>
      ) : (
        selectedCats.map(({ id, label }) => (
          <span key={id} className="chip chipSelected" title={label}>
            {label}
            <button
              type="button"
              className="chipX"
              onClick={() => onChange?.(id)}
              aria-label={`Remove ${label}`}
            >
              ×
            </button>
          </span>
        ))
      )}
    </div>
  );
}

function CategorySelector({ cats, selected, onToggle }) {
  return (
    <div className="catSelector">
      <CategoryPill allCats={cats} selected={selected} onChange={onToggle} />
      <CategorySuggest cats={cats} selected={selected} add={onToggle} />
    </div>
  );
}

function CategorySuggest({ cats, selected, add }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return cats.filter((c) => !selected.includes(c.id));
    return cats.filter((c) => {
      if (selected.includes(c.id)) return false;
      const base = (c.name || "").toLowerCase();
      if (base.includes(needle)) return true;
      if (Array.isArray(c.keywords)) {
        return c.keywords.some((k) =>
          (k || "").toLowerCase().includes(needle)
        );
      }
      return false;
    });
  }, [cats, q, selected]);

  useEffect(() => {
    function onDocClick(e) {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function onKeyDown(e) {
    if (!open) {
      if (e.key === "ArrowDown") {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((prev) =>
        filtered.length === 0 ? 0 : (prev + 1) % filtered.length
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((prev) =>
        filtered.length === 0
          ? 0
          : (prev - 1 + filtered.length) % filtered.length
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[active]) {
        add(filtered[active].id);
        setQ("");
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="catSuggest" ref={boxRef}>
      <input
        className="input"
        placeholder="Search categories"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && (
        <div className="catSuggestList" role="listbox">
          {filtered.length === 0 ? (
            <div className="catSuggestEmpty">No matches.</div>
          ) : (
            filtered.map((c, i) => (
              <div
                key={c.id}
                role="option"
                aria-selected={i === active}
                className={`catSuggestItem ${i === active ? "active" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(c.id);
                }}
                onMouseEnter={() => setActive(i)}
              >
                <div className="catName">{c.name}</div>
                {Array.isArray(c.keywords) && c.keywords.length > 0 && (
                  <div className="catKw">
                    {c.keywords.slice(0, 4).join(" • ")}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}




/* ---------------- BusinessPage ---------------- */
export default function BusinessPage() {
  const { id } = useParams();

  // auth
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); // "admin" | "superadmin" | null

  // data
  const [biz, setBiz] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [cats, setCats] = useState([]);

  // ui/forms
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // form fields
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [island, setIsland] = useState("");
  const [website, setWebsite] = useState("");
  const [telephone, setTelephone] = useState("");
  const [email, setEmail] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [categoryIds, setCategoryIds] = useState([]);
  const [aboutShort, setAboutShort] = useState("");
  const [locations, setLocations] = useState([createEmptyLocation()]);

  const [listingTier, setListingTier] = useState("free");
  const wordLimit = 60;
  const wordCount = aboutShort.trim()
  ? aboutShort.trim().split(/\s+/).length
  : 0;

  // address + map
  const [address, setAddress] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");

  // images/storage
  const storage = fbStorage || getStorage();
  const [logoUrl, setLogoUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [logoProg, setLogoProg] = useState(0);
  const [imgProg, setImgProg] = useState(0);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  async function trackWebsiteClick() {
    if (!biz?.docId) return;
    try {
      await setDoc(doc(db, "business_analytics", biz.docId), {
        businessName: biz.business_name,
        businessID: biz.docId,
        websiteClicks: increment(1),
      }, { merge: true });
    } catch (err) {
      console.error("Website click tracking failed:", err);
    }
  }

  /* Business Traffic */
  useEffect(() => {
    console.log("ANALYTICS EFFECT RUNNING");
    console.log("biz =", biz);
    console.log("biz?.id =", biz?.docId);
    if (!biz) {
      console.log("NO BIZ");
      return;
    }
    
    console.log("biz.docId =", biz.docId);
    if (!biz.docId) {
      console.log("NO DOC ID");
      return;
    }

    console.log("CALLING TRACKVIEW");


    const trackView = async () => {
    try {
      console.log("TRACK VIEW STARTED");
      console.log("biz.docId =", biz.docId);
      console.log("biz.business_name =", biz.business_name);

      const isMobile = /Android|iPhone|iPad/i.test(
        navigator.userAgent
      );

      let source = "direct";

      if (document.referrer.includes("google")) {
        source = "google";
      } else if (document.referrer.includes("facebook")) {
        source = "facebook";
      } else if (document.referrer.includes("instagram")) {
        source = "instagram";
      } else if (document.referrer.includes("whatsapp")) {
        source = "whatsapp";
      } else if (document.referrer) {
        source = "other";
      }

      const analyticsRef = doc(
        db,
        "business_analytics",
        biz.docId
      );

      console.log(
        "Analytics path:",
        `business_analytics/${biz.docId}`
      );

      // Update view counters
      await setDoc(
        analyticsRef,
        {
          businessName: biz.business_name,
          businessID: biz.docId,
          totalViews: increment(1),
          mobileViews: increment(isMobile ? 1 : 0),
          desktopViews: increment(isMobile ? 0 : 1),
          lastViewed: serverTimestamp(),
          [`sources.${source}`]: increment(1),
        },
        { merge: true }
      );

      console.log("Analytics document updated");

      // Create visit log
      await addDoc(
        collection(
          db,
          "business_analytics",
          biz.docId,
          "visits"
        ),
        {
          timestamp: serverTimestamp(),
          source,
          device: isMobile ? "mobile" : "desktop",
          referrer: document.referrer || null,
        }
      );

      console.log("Visit log created");
    } catch (err) {
      console.error("Analytics tracking failed:", err);
    }
  };

    trackView();
  }, [biz?.docId]);

  // watch auth and pull custom role claim
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      if (!u) {
        setRole(null);
        return;
      }
      try {
        const t = await u.getIdTokenResult(true);
        setRole(t.claims.role || null);
      } catch (e) {
        console.error(e);
        setRole(null);
      }
    });
    return () => unsub();
  }, []);

  // load categories (enabled)
  useEffect(() => {
    (async () => {
      const cq = query(collection(db, "categories"), where("enabled", "==", true));
      const snap = await getDocs(cq);
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setCats(list);
    })();
  }, []);

  // load business + contacts
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setMissing(false);

        // business doc
        const refDoc = doc(db, "businesses", id);
        const s = await getDoc(refDoc);
        if (!s.exists()) { setMissing(true); return; }
        const data = {
          docId: s.id,
          ...s.data(),
        };
        console.log("SETTING BIZ", data);
        setBiz(data);

        // prefill form
        setName(data.business_name || "");
        setCountry(data.country || "");
        setIsland(data.island || "");
        setTelephone(data.telephone || "");
        setEmail(data.email || "");
        setWebsite(data.website || "");
        setIsPublished(!!data.is_published);
        setCategoryIds(Array.isArray(data.categoryIds) ? data.categoryIds : []);
        setAboutShort(data.about_short || "");
        setListingTier(data.listingTier || "basic");
        const loadedLocations = getBusinessLocations(data);
        setLocations(loadedLocations.length > 0 ? loadedLocations : [createEmptyLocation()]);

        // address + map prefill
        setAddress(data.locationLabel || "");
        const gp = data.location;
        if (gp && typeof gp.latitude === "number" && typeof gp.longitude === "number") {
          setLat(String(gp.latitude));
          setLng(String(gp.longitude));
        } else {
          setLat(""); setLng("");
        }

        // images prefill
        setLogoUrl(data.logoUrl || "");
        setImageUrl(data.imageUrl || "");

        // contacts (public only)
        try {
          const cq = query(collection(db, "businesses", id, "contacts"), where("public", "==", true));
          const snap = await getDocs(cq);
          const list = [];
          snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
          setContacts(list.filter((c) => c.public));
        } catch { /* ok if none */ }
      } catch (e) {
        console.error(e);
        setMissing(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const isAdminRole = role === "admin" || role === "superadmin";
  const canEdit = useMemo(() => {
    const uid = user?.uid;
    if (!uid || !biz) return false;
    if (isAdminRole) return true;
    return biz.ownerId === uid;
  }, [user, biz, isAdminRole]);

  const canTogglePublished = isAdminRole;

  // unpublished pages are only visible to owner/admin
  if (!loading && !missing && biz && !biz.is_published && !canEdit) {
    return (
      <div>
        <Navbar />
        <main className="bizPage">
          <div className="bizContent">
            <p>Business not found.</p>
            <p><Link to="/directory">Back to directory</Link></p>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <Navbar />
        <main className="bizPage"><div className="bizContent"><p>Loading…</p></div></main>
      </div>
    );
  }

  if (missing || !biz) {
    return (
      <div>
        <Navbar />
        <main className="bizPage">
          <div className="bizContent">
            <p>Business not found.</p>
            <p><Link to="/directory">Back to directory</Link></p>
          </div>
        </main>
      </div>
    );
  }

  /* ---------------- uploads ---------------- */
  async function uploadImage(file, kind) {
    if (!file) return;
    const safeName = file.name.replace(/[^\w.\-]+/g, "_");
    const path = `biz/${id}/${kind}-${Date.now()}-${safeName}`;
    const r = ref(storage, path);
    const setProg = kind === "logo" ? setLogoProg : setImgProg;
    const setUploading = kind === "logo" ? setUploadingLogo : setUploadingImg;
    setProg(0);
    setUploading(true);

    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(r, file);
      task.on(
        "state_changed",
        (snap) => {
          if (snap.totalBytes > 0) {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            setProg(pct);
          }
        },
        (err) => {
          console.error(err);
          setUploading(false);
          reject(err);
        },
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          if (kind === "logo") setLogoUrl(url);
          else setImageUrl(url);
          setUploading(false);
          resolve();
        }
      );
    });
  }
  function onPickLogo(e) {
    const f = e.target.files?.[0];
    if (f) uploadImage(f, "logo").catch(() => {});
  }
  function onPickImage(e) {
    const f = e.target.files?.[0];
    if (f) uploadImage(f, "image").catch(() => {});
  }

  function updateLocation(index, field, value) {
    setLocations((prev) =>
      prev.map((loc, i) => (i === index ? { ...loc, [field]: value } : loc))
    );
  }

  function updateLocationContact(index, field, contactIndex, value) {
    setLocations((prev) => prev.map((loc, i) => {
      if (i !== index) return loc;
      const values = [...(Array.isArray(loc[field]) ? loc[field] : [""])];
      values[contactIndex] = value;
      return { ...loc, [field]: values };
    }));
  }

  function addLocationContact(index, field) {
    setLocations((prev) => prev.map((loc, i) => i === index
      ? { ...loc, [field]: [...(Array.isArray(loc[field]) ? loc[field] : []), ""] }
      : loc));
  }

  function removeLocationContact(index, field, contactIndex) {
    setLocations((prev) => prev.map((loc, i) => {
      if (i !== index) return loc;
      const values = (Array.isArray(loc[field]) ? loc[field] : [""])
        .filter((_, i2) => i2 !== contactIndex);
      return { ...loc, [field]: values.length ? values : [""] };
    }));
  }

  function addLocation() {
    setLocations((prev) => [...prev, createEmptyLocation()]);
  }

  function removeLocation(index) {
    setLocations((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError("");

      const web = (website || "").trim();
      const normalizedLocations = locations
        .map(normalizeLocation)
        .filter(locationHasDetails);
      const savedLocations = normalizedLocations.length
        ? normalizedLocations
        : [
            normalizeLocation({
              country,
              island,
              address,
              telephone,
              email,
              lat,
              lng,
            }),
          ].filter(locationHasDetails);
      const primaryLocation = savedLocations[0] || createEmptyLocation();
      const latNum = parseFloat(primaryLocation.lat);
      const lngNum = parseFloat(primaryLocation.lng);
      const hasCoords = Number.isFinite(latNum) && Number.isFinite(lngNum);


      //fix
      const patch = {
        business_name: name.trim(),
        country: primaryLocation.country || country.trim(),
        island: primaryLocation.island || island.trim(),

        // Contact information
        telephone: primaryLocation.telephone || (telephone || "").trim() || null,
        email: primaryLocation.email || (email || "").trim() || null,
        website: web || null,

        categoryIds: categoryIds,
        about_short: (aboutShort || "").trim() || null,

        updated_at: serverTimestamp(),
        updated_by: user?.uid || null,

        logoUrl: logoUrl || null,
        imageUrl: imageUrl || null,

        location: hasCoords ? new GeoPoint(latNum, lngNum) : null,
        locationLabel: primaryLocation.address || (address || "").trim() || null,
        locations: savedLocations,
      };

      if (isAdminRole) {
        patch.listingTier = listingTier;
      }

      if (canTogglePublished) patch.is_published = !!isPublished;

      await updateDoc(doc(db, "businesses", id), patch);

      //console.log("SETTING BIZ", data);
      setBiz((b) =>
        !b
          ? b
          : {
              ...b,
              business_name: patch.business_name,
              country: patch.country,
              island: patch.island,
              website: patch.website,
              telephone: patch.telephone,
              email: patch.email,
              categoryIds: [...patch.categoryIds],
              about_short: patch.about_short,
              is_published: canTogglePublished ? !!patch.is_published : b.is_published,
              logoUrl: patch.logoUrl,
              imageUrl: patch.imageUrl,
              location: patch.location,
              locationLabel: patch.locationLabel,
              locations: patch.locations,
              ...(isAdminRole
                ? {
                    listingTier,
                    
                  }
                : {}),
            }
      );

      setEditMode(false);
    } catch (e) {
      console.error(e);
      setError(e.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  }

  function toggleCategory(cid) {
    setCategoryIds((prev) =>
      prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]
    );
  }

  if (loading) {
    return (
      <div>
        <Navbar />
        <main className="bizPage">
          <div className="bizContent">
            <p>Loading…</p>
          </div>
        </main>
      </div>
    );
  }

  if (missing || !biz) {
    return (
      <div>
        <Navbar />
        <main className="bizPage">
          <div className="bizContent">
            <p>Business not found.</p>
            <p>
              <Link to="/directory">Back to directory</Link>
            </p>
          </div>
        </main>
      </div>
    );
  }

  const businessLocations = getBusinessLocations(biz);
  const locationMeta = [
    ...new Set(
      businessLocations
        .map((loc) => loc.island || loc.country || loc.region)
        .filter(Boolean)
    ),
  ].join(", ");

  return (
    <div>
      <Navbar />
      <main className="bizPage">
        <div className="bizContent">
          <header className="bizHeader hero">
            {/* Title row with inline square logo on the LEFT */}
            <div className="titleRow">
              {listingTier === "featured" && logoUrl && (
                  <img
                    className="logoInline"
                    src={logoUrl}
                    alt={`${biz.business_name} logo`}
                  />
              )}

              <div>
                <h1 className="bizName">
                  {biz.business_name}
                  {/* {biz.is_featured && <span className="badge">Featured</span>} */}
                  {listingTier === "featured" && (
                    <span className="badge">Featured</span>
                  )}

                </h1>
                <div className="bizMeta">
                  {locationMeta}
                </div>
              </div>
            </div>

            <div className="bizActions">
              {biz.website && !editMode && (
                <a
                  className="BusinessBtn primary linkBtn"
                  href={/^https?:\/\//i.test(biz.website)
                    ? biz.website
                    : `https://${biz.website}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={trackWebsiteClick}
                >
                  Visit website
                </a>
                              )}
              {canEdit && !editMode && (
                <button className="BusinessBtn" onClick={() => setEditMode(true)}>
                  Edit
                </button>
              )}
              {editMode && (
                <>
                  <button
                    className="BusinessBtn primary"
                    onClick={handleSave}
                    disabled={saving || uploadingLogo || uploadingImg}
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    className="BusinessBtn"
                    onClick={() => {
                      setName(biz.business_name || "");
                      setCountry(biz.country || "");
                      setIsland(biz.island || "");
                      setTelephone(biz.telephone || "");
                      setEmail(biz.email || "");
                      setWebsite(biz.website || "");
                      setIsPublished(!!biz.is_published);
                      setCategoryIds(Array.isArray(biz.categoryIds) ? biz.categoryIds : []);
                      setAboutShort(biz.about_short || "");
                      setListingTier(biz.listingTier || "basic");
                      setLogoUrl(biz.logoUrl || "");
                      setImageUrl(biz.imageUrl || "");
                      {
                        const restoredLocations = getBusinessLocations(biz);
                        setLocations(
                          restoredLocations.length > 0
                            ? restoredLocations
                            : [createEmptyLocation()]
                        );
                      }
                      setAddress(biz.locationLabel || "");
                      const gp = biz.location;
                      if (gp && typeof gp.latitude === "number") {
                        setLat(String(gp.latitude));
                        setLng(String(gp.longitude));
                      } else {
                        setLat("");
                        setLng("");
                      }
                      setEditMode(false);
                    }}
                    disabled={saving || uploadingLogo || uploadingImg}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
            {canEdit && (
              <small className="muted">
                {isAdminRole
                  ? "Admin access."
                  : "You own this business profile and can edit the content."}
              </small>
            )}

            {error && <div className="errorMsg">{error}</div>}

            {canTogglePublished && (
              <div className="pubRow">
                <label className="switchLabel">
                  <input
                    type="checkbox"
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                    disabled={saving}
                  />
                  <span>Published</span>
                </label>
                {!isPublished && (
                  <span className="badge badgeDraft">Draft – not visible in directory</span>
                )}
              </div>
            )}
          </header>

          <div className={`bizLayout ${editMode ? "editing" : ""}`}>
            <div className="colMain">
              {!editMode && (
                <>
                  {/* Short description */}
                  {biz.about_short && (
                    <section className="sectionCard">
                      <p className="bizShort">{biz.about_short}</p>
                    </section>
                  )}

                  {/* Categories */}
                  {Array.isArray(biz.categoryIds) && biz.categoryIds.length > 0 && (
                    <section className="sectionCard">
                      <h3>Categories</h3>
                      <CategorySelector
                        cats={cats}
                        selected={biz.categoryIds}
                        onToggle={() => {}}
                      />
                    </section>
                  )}

                  {/* Image (enhanced listings only) */}
                  {listingTier === "featured" && imageUrl && (
                    <section className="mediaRow">
                      <div className="mediaBox wide">
                        <img
                          className="featuredImg"
                          src={imageUrl}
                          alt={`${biz.business_name} image`}
                        />
                        <div className="mediaLabel">Image</div>
                      </div>
                    </section>
                  )}

                  {businessLocations.length > 0 && (
                    <section className="mapBlock sectionCard locationCard">
                      <h3>{businessLocations.length === 1 ? "Location" : "Locations"}</h3>
                      <div className="locationList">
                        {businessLocations.map((loc, index) => {
                          const embed = locationMapEmbed(loc) || (index === 0 ? biz.mapUrl : null);
                          const title = loc.region || loc.island || loc.country || `Location ${index + 1}`;

                          return (
                            <div className="locationItem" key={`${title}-${index}`}>
                              <div className="locationTitle">{title}</div>
                              {loc.address && (
                                <div className="muted" style={{ marginBottom: 6 }}>
                                  {loc.address}
                                </div>
                              )}
                              <div className="locationContact">
                                {normalizeContactList(loc.telephones, loc.telephone).map((phone) => (
                                  <a key={phone} href={`tel:${phone}`}>{phone}</a>
                                ))}
                                {normalizeContactList(loc.emails, loc.email).map((address) => (
                                  <a key={address} href={`mailto:${address}`}>{address}</a>
                                ))}
                              </div>
                              {embed ? (
                                <div className="mapFrame">
                                  <iframe
                                    title={`Map location ${index + 1}`}
                                    src={embed}
                                    loading="lazy"
                                  />
                                </div>
                              ) : (
                                <div className="muted">No map coordinates set.</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  )}

                  {biz.description && (
                    <section className="sectionCard">
                      <p className="bizDesc">{biz.description}</p>
                    </section>
                  )}
                </>
              )}

              {editMode && (
                <>
                  {/* About & basics form when editing */}
                  <section className="sectionCard">
                    <h3>Basics</h3>
                    <div className="fieldGrid two">
                      <div className="field">
                        <label>Business name</label>
                        <input
                          className="input"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                      <div className="field">
                        <label>Website</label>
                        <input
                          className="input"
                          value={website}
                          onChange={(e) => setWebsite(e.target.value)}
                          placeholder="example.com"
                        />
                        <div className="hint">
                          Don’t include “http://” – we’ll add that automatically.
                        </div>
                      </div>
                    </div>

                    <section className="locationEditor">
                      <div className="locationEditorHeader">
                        <h3>Locations</h3>
                        <button className="BusinessBtn" type="button" onClick={addLocation}>
                          Add location
                        </button>
                      </div>

                      {locations.map((loc, index) => (
                        <div className="locationEditCard" key={index}>
                          <div className="locationEditorHeader">
                            <h4>Location {index + 1}</h4>
                            {locations.length > 1 && (
                              <button
                                className="BusinessBtn"
                                type="button"
                                onClick={() => removeLocation(index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>

                          <div className="fieldGrid thirds">
                            <div className="field">
                              <label>Country</label>
                              <input
                                className="input"
                                value={loc.country}
                                onChange={(e) =>
                                  updateLocation(index, "country", e.target.value)
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Island</label>
                              <input
                                className="input"
                                value={loc.island}
                                onChange={(e) =>
                                  updateLocation(index, "island", e.target.value)
                                }
                              />
                            </div>
                          </div>

                          <div className="fieldGrid two">
                            <div className="field">
                              <label>Telephones</label>
                              {(loc.telephones || [loc.telephone || ""]).map((phone, contactIndex) => (
                                <div className="contactInputRow" key={contactIndex}>
                                  <input className="input" type="tel" value={phone} onChange={(e) => updateLocationContact(index, "telephones", contactIndex, e.target.value)} />
                                  {(loc.telephones || [""]).length > 1 && <button className="BusinessBtn" type="button" onClick={() => removeLocationContact(index, "telephones", contactIndex)}>Remove</button>}
                                </div>
                              ))}
                              <button className="BusinessBtn" type="button" onClick={() => addLocationContact(index, "telephones")}>Add telephone</button>
                            </div>
                            <div className="field">
                              <label>Emails</label>
                              {(loc.emails || [loc.email || ""]).map((address, contactIndex) => (
                                <div className="contactInputRow" key={contactIndex}>
                                  <input className="input" type="email" value={address} onChange={(e) => updateLocationContact(index, "emails", contactIndex, e.target.value)} />
                                  {(loc.emails || [""]).length > 1 && <button className="BusinessBtn" type="button" onClick={() => removeLocationContact(index, "emails", contactIndex)}>Remove</button>}
                                </div>
                              ))}
                              <button className="BusinessBtn" type="button" onClick={() => addLocationContact(index, "emails")}>Add email</button>
                            </div>
                          </div>

                          <div className="fieldGrid two">
                            <div className="field">
                              <label>Address</label>
                              <AddressAutocomplete
                                value={loc.address}
                                onChange={(value) =>
                                  updateLocation(index, "address", value)
                                }
                                onPick={(plat, plng, formatted) => {
                                  updateLocation(index, "lat", String(plat));
                                  updateLocation(index, "lng", String(plng));
                                  if (formatted) {
                                    updateLocation(index, "address", formatted);
                                  }
                                }}
                              />
                            </div>
                            <div className="field">
                              <label>Map coordinates</label>
                              <div className="coordRow">
                                <input
                                  className="input"
                                  placeholder="Latitude"
                                  value={loc.lat}
                                  onChange={(e) =>
                                    updateLocation(index, "lat", e.target.value)
                                  }
                                />
                                <input
                                  className="input"
                                  placeholder="Longitude"
                                  value={loc.lng}
                                  onChange={(e) =>
                                    updateLocation(index, "lng", e.target.value)
                                  }
                                />
                              </div>
                              <div className="hint">Click on the map to set coordinates.</div>
                            </div>
                          </div>

                          <MapPicker
                            lat={parseFloat(loc.lat) || null}
                            lng={parseFloat(loc.lng) || null}
                            onPick={(plat, plng) => {
                              updateLocation(index, "lat", String(plat));
                              updateLocation(index, "lng", String(plng));
                            }}
                          />
                        </div>
                      ))}
                    </section>

                    {/* Listing tier (admin only) */}
                    {isAdminRole && (
                      <div className="field">
                        <label>Listing tier</label>
                        <select
                          className="input"
                          value={listingTier}
                          onChange={(e) => setListingTier(e.target.value)}
                        >
                          <option value="free">
                            Free listing 
                          </option>
                          <option value="basic">
                            Basic listing 
                          </option>
                          <option value="featured">
                            Featured listing (priority placement)
                          </option>
                        </select>
                        <div className="hint">
                          Featured listings can use a logo/image and appear higher in search
                          results.
                        </div>
                      </div>
                    )}

                    {/* About */}
                    <div className="field">
                      <label>About ({wordLimit} words max)</label>
                      <textarea
                        className="input textarea wide"
                        rows={3}
                        draggable={false}
                        value={aboutShort}
                        onChange={(e) => {
                          const text = e.target.value;
                          const words = text.trim().split(/\s+/).filter(Boolean);
                          if (words.length <= wordLimit) {
                            setAboutShort(text);
                          } else {
                            const clipped = words.slice(0, wordLimit).join(" ");
                            setAboutShort(clipped);
                          }
                        }}
                        placeholder="Tell customers what makes your business special…"
                      />
                      <div className="charCount">
                        {wordCount}/{wordLimit} words
                      </div>
                    </div>

                    {/* Categories */}
                    <div className="field">
                      <label>Categories</label>
                      <CategorySelector
                        cats={cats}
                        selected={categoryIds}
                        onToggle={toggleCategory}
                      />
                    </div>
                  </section>

                  {/* Images */}
                  <section className="sectionCard">
                    <h3>Images</h3>
                    <div className="fieldGrid two">
                      <div className="field wideTwo">
                        <label>Logo</label>
                        <div className="uploadCard">
                          {logoUrl ? (
                            <img className="logoPreview" src={logoUrl} alt="Logo preview" />
                          ) : (
                            <div className="uploadPlaceholder">No logo selected</div>
                          )}
                          <div className="uploadActions">
                            <label className="BusinessBtn">
                              Choose file
                              <input
                                type="file"
                                accept="image/*"
                                onChange={onPickLogo}
                                hidden
                              />
                            </label>
                            {uploadingLogo && (
                              <span className="muted">{logoProg}%</span>
                            )}
                            {logoUrl && (
                              <button
                                className="BusinessBtn"
                                onClick={() => setLogoUrl("")}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="field wideTwo">
                        <label>Featured image</label>
                        <div className="uploadCard">
                          {imageUrl ? (
                            <img
                              className="featuredPreview"
                              src={imageUrl}
                              alt="Featured"
                            />
                          ) : (
                            <div className="uploadPlaceholder">
                              No featured image selected
                            </div>
                          )}
                          <div className="uploadActions">
                            <label className="BusinessBtn">
                              Choose file
                              <input
                                type="file"
                                accept="image/*"
                                onChange={onPickImage}
                                hidden
                              />
                            </label>
                            {uploadingImg && (
                              <span className="muted">{imgProg}%</span>
                            )}
                            {imageUrl && (
                              <button
                                className="BusinessBtn"
                                onClick={() => setImageUrl("")}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </>
              )}
            </div>

            <aside className="colSide">
              {contacts.length > 0 && (
                <section className="sectionCard contactCard">
                  <h3>Contact</h3>

                  <ul className="contactList">
                    {biz.telephone && (
                      <li>
                        <span className="contactLabel">Phone</span>
                        <a href={`tel:${biz.telephone}`}>
                          {biz.telephone}
                        </a>
                      </li>
                    )}

                    {biz.email && (
                      <li>
                        <span className="contactLabel">Email</span>
                        <a href={`mailto:${biz.email}`}>
                          {biz.email}
                        </a>
                      </li>
                    )}

                    {biz.website && (
                      <li>
                        <span className="contactLabel">Website</span>
                        <a
                          href={
                            /^https?:\/\//i.test(biz.website)
                              ? biz.website
                              : `https://${biz.website}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          onClick={trackWebsiteClick}
                        >
                          {biz.website}
                        </a>
                      </li>
                    )}
                  </ul>
                </section>




              )}

            </aside>
          </div>
        </div>
      </main>
    </div>
  );
}
